import type { Scene } from '@babylonjs/core/scene';
import type { TargetCamera } from '@babylonjs/core/Cameras/targetCamera';
import { Vector3 } from '@babylonjs/core/Maths/math.vector';
import type { Mesh } from '@babylonjs/core/Meshes/mesh';
import { CreatePlane } from '@babylonjs/core/Meshes/Builders/planeBuilder';
import { PBRMaterial } from '@babylonjs/core/Materials/PBR/pbrMaterial';
import { DynamicTexture } from '@babylonjs/core/Materials/Textures/dynamicTexture';
import { Texture } from '@babylonjs/core/Materials/Textures/texture';
import { Color3 } from '@babylonjs/core/Maths/math.color';
import type { AdvertisingConfig } from '../config/advertising.config';
import { forwardOf } from '../level/connectors/Pose';
import type { TrackPresenter } from '../level/TrackPresenter';
import { applyDepthOffset } from '../rendering/decals';
import { stretchFog } from '../rendering/fogStretch';
import type { AdBinding, AdSlotBinder } from './AdSlotBinder';
import { AD_FORMATS, coverCrop, type AdCreative, type AdSlotType } from './AdTypes';

interface Surface {
  readonly binding: AdBinding;
  readonly mesh: Mesh;
  /** Direction the visible side faces (world yaw) – includes the path heading on curves. */
  readonly facingYaw: number;
}

/** Matte printed PVC (1 since 2026-10-04: no glossy sheen in the sun). */
const BANNER_ROUGHNESS = 1;
/** Sky reflection on the banner (Babylon default 1). */
const BANNER_ENVIRONMENT = 0.6;
/** Albedo factor: white prints stay white in shade but no longer blow out in direct sun. */
const BANNER_ALBEDO = 0.82;

/**
 * Renders ad slots as separate textured surfaces (never part of gameplay
 * collision). Creatives are swapped by texture only – no GLB or level
 * rebuild. Also samples visibility for impression counting.
 */
export class AdSurfacePresenter {
  private readonly surfaces = new Map<string, Surface>();
  private readonly materials = new Map<string, PBRMaterial>();
  private readonly planePool = new Map<string, Mesh[]>();
  private sampleTimer = 0;
  private readonly toCamera = new Vector3();

  constructor(
    private readonly scene: Scene,
    private readonly binder: AdSlotBinder,
    private readonly track: TrackPresenter,
    private readonly camera: TargetCamera,
    private readonly config: AdvertisingConfig,
  ) {
    binder.onBound((binding) => this.show(binding));
    binder.onUnbound((binding) => this.hide(binding));
  }

  /** Surface meshes – used by the debug view. */
  meshes(): Mesh[] {
    return [...this.surfaces.values()].map((s) => s.mesh);
  }

  update(dt: number): void {
    this.sampleTimer += dt;
    if (this.sampleTimer < this.config.visibilitySampleInterval) return;
    const elapsed = this.sampleTimer;
    this.sampleTimer = 0;
    const camPos = this.camera.globalPosition;
    const halfFovTan = Math.tan(this.camera.fov / 2);

    for (const { binding, mesh, facingYaw } of this.surfaces.values()) {
      const center = mesh.getAbsolutePosition();
      this.toCamera.copyFrom(camPos).subtractInPlace(center);
      const distance = this.toCamera.length();
      const normal = forwardOf(facingYaw);
      const facing = normal.x * this.toCamera.x + normal.z * this.toCamera.z > 0;
      const visible = distance <= this.config.maxVisibleDistance && facing && this.camera.isInFrustum(mesh);
      const screenFraction = distance > 0 ? binding.slot.height / (2 * distance * halfFovTan) : 0;
      this.binder.reportVisibility(binding.id, visible, screenFraction, elapsed);
    }
  }

  private show(binding: AdBinding): void {
    const { slot } = binding;
    const mesh = this.acquirePlane(slot.width, slot.height);
    mesh.parent = this.track.rootOf(binding.segment);
    // Slot data is in path coordinates – on curves the banner follows the arc.
    const at = binding.segment.placement(slot.position);
    mesh.position.set(at.x, at.y, at.z);
    // CreatePlane faces −Z; rotate so the visible side faces the slot direction.
    mesh.rotation.y = at.yaw + slot.yaw + Math.PI;
    mesh.material = this.materialFor(binding.creative, slot.slotType);
    mesh.setEnabled(true);
    this.surfaces.set(binding.id, { binding, mesh, facingYaw: binding.segment.pose.yaw + at.yaw + slot.yaw });
  }

  private hide(binding: AdBinding): void {
    const surface = this.surfaces.get(binding.id);
    if (!surface) return;
    surface.mesh.setEnabled(false);
    surface.mesh.parent = null;
    this.planePool.get(sizeKey(binding.slot.width, binding.slot.height))?.push(surface.mesh);
    this.surfaces.delete(binding.id);
  }

  private acquirePlane(width: number, height: number): Mesh {
    const key = sizeKey(width, height);
    let list = this.planePool.get(key);
    if (!list) {
      list = [];
      this.planePool.set(key, list);
    }
    const mesh = list.pop() ?? CreatePlane(`AD_SURFACE_${key}`, { width, height }, this.scene);
    mesh.isPickable = false;
    return mesh;
  }

  private materialFor(creative: AdCreative, slotType: AdSlotType): PBRMaterial {
    const key = `${creative.creativeId}|${slotType}`;
    const cached = this.materials.get(key);
    if (cached) return cached;

    const target = new PBRMaterial(`MAT_AD_${key}`, this.scene);
    const style = this.config.surfaceStyles[slotType];
    const show = (texture: Texture) => {
      if (style) texture.anisotropicFilteringLevel = style.anisotropicFilteringLevel;
      target.albedoTexture = texture;
      target.emissiveTexture = texture;
    };
    if (creative.textureUrl) {
      // A broken creative URL (backend, CDN) must not leave a black board – fall back to the label.
      const onError = () => show(this.placeholderTexture(creative, slotType));
      const texture = new Texture(creative.textureUrl, this.scene, false, true, Texture.TRILINEAR_SAMPLINGMODE, null, onError);
      if (creative.sourceAspect) {
        // a poster of another format (sponsored game, every surface): its centre in this surface's ratio, never stretched
        const crop = coverCrop(creative.sourceAspect, AD_FORMATS[slotType].aspectRatio);
        texture.uScale = crop.uScale;
        texture.vScale = crop.vScale;
        texture.uOffset = crop.uOffset;
        texture.vOffset = crop.vOffset;
      }
      show(texture);
    } else {
      show(this.placeholderTexture(creative, slotType));
    }
    // Printed PVC banner: lit by sun and sky like the fence it hangs on; a little
    // self-illumination keeps it readable in shadow (more on floodlit styles, e.g. the gantry).
    const glow = style?.glow ?? this.config.bannerGlow;
    target.metallic = 0;
    target.roughness = BANNER_ROUGHNESS;
    // Matte print, no glare (user 2026-10-04: white / glossy photos "spiegeln zu extrem, zu grell", only in full sun):
    // no specular sun highlight and dimmer sky reflection, and the albedo a little below 1 – sunlight plus the
    // self-illumination no longer drive white areas past white. In shadow and at night the glow keeps them readable.
    target.metallicF0Factor = 0;
    target.environmentIntensity = BANNER_ENVIRONMENT;
    target.albedoColor = new Color3(BANNER_ALBEDO, BANNER_ALBEDO, BANNER_ALBEDO);
    target.emissiveColor = new Color3(glow, glow, glow);
    // a few centimetres in front of fence, wall or scaffold: no z-fighting in the distance
    applyDepthOffset(target, this.config.depthOffsetLevel);
    if (style) stretchFog(target, style.fogScale);
    this.materials.set(key, target);
    return target;
  }

  private placeholderTexture(creative: AdCreative, slotType: AdSlotType): DynamicTexture {
    const width = this.config.surfaceStyles[slotType]?.placeholderTextureWidth ?? this.config.placeholderTextureWidth;
    const height = Math.round(width / AD_FORMATS[slotType].aspectRatio);
    const texture = new DynamicTexture(`TEX_AD_${creative.creativeId}`, { width, height }, this.scene, true);
    // In browsers DynamicTexture is backed by a real 2D canvas (Babylon's interface omits text alignment).
    const ctx = texture.getContext() as unknown as CanvasRenderingContext2D;
    const border = Math.round(height * 0.06);
    ctx.fillStyle = creative.textColor ?? '#ffffff';
    ctx.fillRect(0, 0, width, height);
    ctx.fillStyle = creative.backgroundColor ?? '#333333';
    ctx.fillRect(border, border, width - border * 2, height - border * 2);

    ctx.fillStyle = creative.textColor ?? '#ffffff';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    let fontSize = Math.round(height * 0.38);
    ctx.font = `800 ${fontSize}px Arial, sans-serif`;
    while (ctx.measureText(creative.label).width > width * 0.86 && fontSize > 8) {
      fontSize -= 2;
      ctx.font = `800 ${fontSize}px Arial, sans-serif`;
    }
    ctx.fillText(creative.label, width / 2, height * (creative.showSlotType ? 0.46 : 0.52));
    if (creative.showSlotType) {
      ctx.font = `600 ${Math.round(height * 0.11)}px Arial, sans-serif`;
      ctx.fillText(slotType, width / 2, height * 0.8);
    }
    texture.update();
    return texture;
  }
}

function sizeKey(width: number, height: number): string {
  return `${width}x${height}`;
}
